import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as healthCoachActions from "../../../store/slices/HealthCoachSlice";
import { DEFAULT_CARE_COINS } from "../../../data/nivoConsts";
import { RootState } from "../../../store/store";
import moment from "moment";
import { getEncryptedTextV2, getUuid } from "../../../utils/common";
import { toast } from "react-toastify";
import { constants } from "../../../constants/common";
import { API, handleEncryptedResponse } from "../../../services/rest/base";
import { useNavigate } from "react-router-dom";

export interface SessionTableProps {
  formObject: any;
  setFormObject: (formObject: any) => void;
  cycleCompleted: number;
  isNivo?: boolean;
  formData: any;
  detailsObject: any;
  updateFormObject: (key: string, value: any) => void;
}
const useSessionTable = (props: SessionTableProps) => {
  const secretKey = process.env.REACT_APP_HEALTH_SECRET_KEY || "";
  const uploadPayloadRef = useRef<any>(null);
  const [submissionLoading, setSubmissionLoading] = useState<any>(false);
  const [submissionStatus, setSubmissionStatus] = useState<string>("");
  const navigate = useNavigate();
  const { formObject, setFormObject, cycleCompleted, formData, detailsObject } =
    props;
  const dispatch = useDispatch();

  const {
    patientVisitDetailsState: {
      isLoading: fetchingVisitDetails,
      data: visitDetailsData,
    },
    bulkScheduleVisitDetailsState: { isLoading, data, errors },
    scheduleCycleState: { isLoading: scheduling, data: scheduledCycleData },
  } = useSelector((state: RootState) => state.healthCoach);

  useEffect(() => {
    // if (!isLoading && !errors && data) {
    // Called after successful bulk schedule visit details
    // called before bulk submission or creation of schedule visits
    dispatch(
      healthCoachActions.getPatientVisitDetailsRequest({
        patient_id: formObject.patient_id,
        records_needed: 1,
      }),
    );
    // }
  }, [isLoading, errors, data]);

  useEffect(() => {
    // Called after successfully scheduling/creating for upcoming cycle
    if (scheduledCycleData) {
      setSubmissionLoading(false);
      navigateToVerifiedPatients();
      dispatch(healthCoachActions.resetScheduleCycle());
      dispatch(healthCoachActions.resetBulkScheduleVisitDetails());
    }
  }, [scheduling, scheduledCycleData]);

  useEffect(() => {
    if (
      visitDetailsData &&
      visitDetailsData?.length > 0 &&
      !fetchingVisitDetails
    ) {
      const newVisits = visitDetailsData?.map((visit: any) => ({
        visit_cycle: visit.visit_cycle,
        follow_up_date: visit.visit_date
          ? moment(visit.visit_date).format("YYYY-MM-DD")
          : "",
        dosage: visit?.visit_meta_info?.dosage || "",
        therapy_type: visit?.visit_meta_info?.therapy_type || "",
        invoice: "",
        approved: visit?.visit_meta_info?.rewards_assigned || false,
        visit_id: visit.patient_visit_id,
        // Add other fields as needed, mapping or initializing as required
      }));
      setFormObject((prev: any) => ({
        ...prev,
        patientVisits: newVisits,
        // patient visits initialized from backend
        // after bulk schedule visit details, now go for calling upload bulk records
        _initializedFromBackend: true,
      }));
    }
  }, [visitDetailsData, fetchingVisitDetails]);

  useEffect(() => {
    if (visitDetailsData && visitDetailsData?.length > 0) return;
    if (
      !formObject.patientVisits ||
      formObject.patientVisits.length !== cycleCompleted
    ) {
      const newVisits = Array.from({ length: cycleCompleted }, (_, i) => ({
        visit_cycle: i + 1,
        follow_up_date: "",
        dosage: "",
        invoice: "",
        approved: false,
        visit_id: getUuid(),
      }));
      setFormObject((prev: any) => ({
        ...prev,
        patientVisits: newVisits,
      }));
    }
  }, [cycleCompleted, setFormObject, visitDetailsData]);

  useEffect(() => {
    if (!formObject?._initializedFromBackend) return;

    const run = async () => {
      // Remove the flag completely after handling bulk upload patient records
      setFormObject((prev: any) => {
        const { _initializedFromBackend, ...rest } = prev;
        return rest;
      });
      setSubmissionStatus("Uploading records for previous patient visits/cycles...");
      await delay(1000); // delay added
      await handleBulkUploadPatientRecords(uploadPayloadRef.current);
    };

    run();
  }, [formObject?._initializedFromBackend]);

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const navigateToVerifiedPatients = () => {
    const token = detailsObject.token ?? localStorage.getItem("token");
    navigate(`/all-patients/:token`, {
      state: {
        token: token,
        journey: "verified-patients",
      },
    });
  };

  const handleAddRow = () => {
    setFormObject((prev: any) => {
      const visits = [...prev.patientVisits];
      if (visits?.length === 0) return prev;

      const lastIndex = visits?.length - 1;
      const lastVisit = visits?.[lastIndex];

      // Remove discharge summary from last visit
      const { ds, ...restLastVisit } = lastVisit;

      visits[lastIndex] = restLastVisit;

      const newVisit = {
        visit_cycle: visits?.length + 1,
        follow_up_date: "",
        dosage: "",
        invoice: "",
        approved: false,
        visit_id: getUuid(),
      };

      return {
        ...prev,
        patientVisits: [...visits, newVisit],
      };
    });
  };

  const handleRemoveRow = (idxToRemove: number) => {
    if (formObject.patientVisits.length === 1 || visitDetailsData?.length > 0)
      return;
    setFormObject((prev: any) => {
      const visits = [...prev.patientVisits];
      const lastIndex = visits?.length - 1;
      const isRemovingLast = idxToRemove === lastIndex;

      // Step 1: Remove selected row
      let updatedVisits = visits?.filter(
        (_: any, idx: number) => idx !== idxToRemove,
      );

      // Step 2: If last row removed → clean pi & ds from new last
      if (isRemovingLast && updatedVisits?.length > 0) {
        const newLastIndex = updatedVisits?.length - 1;
        const { pi, ds, ...restVisit } = updatedVisits?.[newLastIndex];

        updatedVisits[newLastIndex] = restVisit;
      }

      // Step 3: Reassign visit_cycle
      updatedVisits = updatedVisits?.map((visit: any, i: number) => ({
        ...visit,
        visit_cycle: i + 1,
      }));

      return {
        ...prev,
        patientVisits: updatedVisits,
      };
    });
  };

  const handleUpcomingChange = (field: string, value: string) => {
    setFormObject((prev: any) => ({
      ...prev,
      upcomingSession: { ...prev.upcomingSession, [field]: value },
    }));
  };

  const handleSessionChange = (
    idx: number,
    field: string,
    value: string | boolean,
  ) => {
    setFormObject((prev: any) => ({
      ...prev,
      patientVisits: prev.patientVisits.map((session: any, i: number) =>
        i === idx ? { ...session, [field]: value } : session,
      ),
    }));
  };

  const handleProceed = () => {
    const { patientVisits, upcomingSession, ...rest } = formObject;
    const {
      notes,
      approval,
      therapy_indication,
      therapy: therapy_type,
      dischargeSummary,
      ...rootFormObject
    } = rest;

    const payload = {
      ...rootFormObject,
      therapy_type,
      therapy_indication,
      cancer_type: formData?.cancerType,
      target_therapy_drug: formData?.primaryDrug,
      support_program_drug: formData?.primaryDrug,
      primary_drugs: [
        {
          drug_name: formData?.primaryDrug,
          is_free: formData?.primaryDrugPaidStatus,
        },
      ],
      secondary_drugs: formData?.secondaryDrugs,

      patientVisits: patientVisits.map((visit: any) => {
        const { follow_up_date, approved, invoice, visit_id, pi, ds, ...restVisitInfo } = visit;
        return {
          follow_up_date,
          visit_meta_info: {
            ...restVisitInfo,
            rewards_assigned: approved ? 1 : 0,
          },
          visit_id, // send visit_id to upload bulk records
          visit_status: "VA", //restVisitInfo?.visit_cycle !== patientVisits.length ? "VA" : "N",
          ...(approved
            ? {
                assign_reward: 1,
                assign_reward_params: {
                  amount: parseInt(`${DEFAULT_CARE_COINS}00`),
                  transactionType: "DOCUMENT_UPLOAD",
                  notes: {
                    type: "visit_status_approved",
                  },
                  patientId: rootFormObject.patient_id,
                },
              }
            : { assign_reward: 0 }),
        };
      }),
    };
    dispatch(healthCoachActions.bulkScheduleVisitDetailsRequest(payload));
  };

  const handleCreateUpcomingSession = () => {
    // Build primary_drugs array with is_free inside each drug object
    const primaryDrugs = [
      {
        drug_name: formData.primaryDrug,
        is_free: String(formData.primaryDrugPaidStatus || 0), // Ensure string: "0" = Paid, "1" = Free
      },
    ];

    // Build secondary_drugs array if any are selected
    // Each drug now has its own is_free status
    // Filter out empty rows and map to clean objects with only drug_name and is_free
    const secondaryDrugs =
      formData.secondaryDrugs && formData.secondaryDrugs.length > 0
        ? formData.secondaryDrugs
            .filter((drug: any) => drug.drug_name && drug.drug_name !== "")
            .map((drug: any) => ({
              drug_name: drug.drug_name,
              is_free: String(drug.is_free !== undefined ? drug.is_free : 0), // Ensure string
            }))
        : [];

    const hasSecondaryDrugs = secondaryDrugs.length > 0;

    // Build visit_meta_info object
    const visitMetaInfo: any = {
      target_therapy_drug: formData.primaryDrug,
      primary_drugs: primaryDrugs,
      therapy_type: formObject.therapy,
      visit_cycle: formObject.patientVisits.length + 1,
      therapy_indication: formObject.therapy_indication,
      cancer_type: formData.cancerType,
      notes: formObject.upcomingSession.notes,
      // Primary drug paid/free status
      is_primary_drug_free: formObject.upcomingSession.is_drug_free,
    };

    // Only include therapy_indication if it has a value (not empty string)
    // This means the therapy has indications and user selected one
    if (formData.indication && formData.indication !== "") {
      visitMetaInfo.therapy_indication = formData.indication;
    }

    // Add dosage for Nivolumab
    if (formData.primaryDrug === "nivolumab" && formData.upcoming_dosage) {
      visitMetaInfo.dosage = formData.upcoming_dosage;
    }

    // Add secondary drugs array if any are selected
    // Each drug object already contains drug_name and is_free
    if (hasSecondaryDrugs) {
      visitMetaInfo.secondary_drugs = secondaryDrugs;
    }

    const payload = {
      patient_id: detailsObject.patientData.patient_id,
      patient_phone: detailsObject.patientData.contact_no,
      patient_name: detailsObject.patientData.name,
      updated_by: detailsObject.userDetails.health_coach_id,
      follow_up_date: formObject.upcomingSession.follow_up_date,
      support_program_drug: formData.primaryDrug,
      visit_meta_info: visitMetaInfo,
      visit_status: "N",
      ...(formData.isRewardsAssign
        ? {
            assign_reward: 1,
            assign_reward_params: {
              amount: parseInt(`${detailsObject.coins}00`),
              transactionType: "DOCUMENT_UPLOAD",
              notes: {
                type: "visit_status_approved",
              },
              patientId: detailsObject.patientData.patient_id,
            },
          }
        : { assign_reward: 0 }),
    };

    dispatch(
      healthCoachActions.scheduleCycleRequest({
        ...payload,
        isForBulkProcess: true, // show a different toast msg in this case when it is called from here
      }),
    );
  };

  const handleAddDocument = (
    idx: number,
    document: any,
    record: any,
    type: string,
  ) => {
    // Called when a document is added to a visit
    setFormObject((prev: any) => ({
      ...prev,
      patientVisits: prev.patientVisits.map((visit: any, i: number) =>
        (visit.visit_id &&
          record?.visit_id &&
          visit.visit_id === record?.visit_id) ||
        (!visit.visit_id && i === idx)
          ? { ...visit, [type]: document }
          : visit,
      ),
    }));
  };

  const handleError = (err: string) => {
    toast.error(err || constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG);
  };

  const handleBulkUploadPatientRecords = async (recordsPayload: any) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    // Append all files from recordsPayload.records
    let files: any = [];
    if (recordsPayload?.records && Array.isArray(recordsPayload.records)) {
      recordsPayload.records.forEach((record: any) => {
        if (record.file) {
          files.push(record.file);
          delete record.file; // Remove the file key from the record
        }
      });
    }
    files.forEach((file: any) => {
      formData.append("file", file[0]);
    });
    formData.append("payload", getEncryptedTextV2(recordsPayload));

    try {
      await fetch(`${API}/healthcoach/bulk_upload_patient_records`, {
        method: "POST",
        body: formData,
        headers: {
          health_secret: secretKey,
          Token: `${token}`,
        },
      })
        .then(async (res) => {
          const response = await handleEncryptedResponse(res);
          if (Number(response.code) === 1) {
            // toast.success(
            //   response?.message || "Documents Uploaded successfully",
            // );
            uploadPayloadRef.current = null; // reset upload payload reference
            // call create upcoming session after successful bulk upload patient records
            setSubmissionStatus("Creating upcoming patient visit/cycle...");
            await delay(1000); // delay added
            handleCreateUpcomingSession();
          } else {
            handleError(response.message);
          }
        })
        .catch((errors) => {
          handleError(errors.message);
        })
        .finally(() => {
        });
    } catch (err: any) {
      handleError(err?.message);
      console.error("errr", err);
    }
  };

  const buildUploadRecordsPayload = () => {
    const records: any[] = [];
    const noOfVisits = formObject?.patientVisits?.length;
    for (let i = noOfVisits - 1; i >= 0; i--) {
      const visit = formObject?.patientVisits?.[i];
      const commonPayload = {
        //document_data: [`${visit?.visit_id}-New-Record-${moment().unix()}-${i}.pdf`],
        container_account: "aztatvaprodstorage",
        container_name: "patient-reports",
        meta_data: {
          report_status: "Approved",
          visit_id: visit?.visit_id,
          onco_cycle_report: "1", // visit.visit_cycle,
        },
      };
      // Upload Purchase Invoice for all visits
      records.push({
        file: [visit?.pi],
        title: "Purchase Invoice",
        test_name: "Invoice",
        extension_type: visit?.pi?.type?.split("/")?.pop(),
        document_data: [`${visit?.visit_id}-New-Record-${Date.now()}${i}.pdf`],
        ...commonPayload,
      });
      // Upload Discharge Summary for last visit only
      if (visit?.visit_cycle === noOfVisits) {
        records.push({
          file: [visit?.ds],
          title: "Discharge Summary",
          test_name: "Discharge Summary",
          extension_type: visit?.ds?.type?.split("/")?.pop(),
          document_data: [`${visit?.visit_id}-New-Record-${Date.now()}${i+1}.pdf`],
          ...commonPayload,
        });
      }
    }
    return {
        patient_id: detailsObject?.patientData?.patient_id,
        records,
      };;
  };

  const handleBulkSubmission = async () => {
    setSubmissionLoading(true);
    setSubmissionStatus("Creating previous patient visit(s)/cycle(s)...");
    const uploadPayload = buildUploadRecordsPayload();
    uploadPayloadRef.current = uploadPayload;
    await delay(1000); // 1 second delay
    handleProceed();
  };

  // Validation: All required fields in patientVisits must be filled (previous session table)
  const isSessionFormValid = useMemo(() => {
    if (!formObject.patientVisits || formObject.patientVisits.length === 0)
      return false;
    // Check each visit for required fields (customize as needed)
    if (!formObject?.therapy) return false;
    if (props.isNivo && !formObject.therapy_indication) return false;
    return formObject.patientVisits.every((visit: any) => {
      // Add more required fields as needed
      return (
        visit.follow_up_date &&
        (!props.isNivo || (visit.dosage && visit.dosage > 0)) // dosage required if isNivo
      );
    });
  }, [formObject, props.isNivo, formData]);

  // Validation: All required fields for upcoming session must be filled and date must be today or later
  const isUpcomingSessionValid = useMemo(() => {
    const { upcomingSession } = formObject;
    if (!upcomingSession) return false;
    const { follow_up_date, dosage, notes } = upcomingSession;

    if (
      !follow_up_date ||
      (!notes && notes !== "") ||
      (props.isNivo && !dosage && dosage !== 0)
    ) {
      return false;
    }
    return true;
  }, [formObject, props.isNivo]);

  // Validation: All required documents must be uploaded for each visit
  const isDocumentUploadValid = useMemo(() => {
    const noOfVisits = formObject?.patientVisits?.length;
    for (let i = 0; i < noOfVisits; i++) {
      const visit = formObject?.patientVisits?.[i];
      if (visit?.visit_cycle === noOfVisits) {
        if (!visit?.pi || !visit?.ds) return false; // Discharge Summary is required for last visit only
      } else {
        if (!visit?.pi) return false; // Purchase Invoice is required for all visits, including last visit
      }
    }
    return true;
  }, [formObject.patientVisits]);

  return {
    handleAddRow,
    handleRemoveRow,
    handleAddDocument,
    isSessionFormValid,
    handleSessionChange,
    handleUpcomingChange,
    isDocumentUploadValid,
    isUpcomingSessionValid,
    handleBulkSubmission,
    submissionLoading,
    submissionStatus
  };
};

export default useSessionTable;
